import throttle from 'lodash/throttle';
import { useMemo } from 'react';
import { useEffectOnce } from 'react-use';

import { pubSub } from 'utils/pubSub';

type UseDashboardRefetchEffectOptions = {
  refetch: () => unknown;
};

/**
 * A hook that listens to a pubSub event, and calls the provided `refetch`
 * function with throttle to refresh the dashboard data.
 */
export const useDashboardRefetchEffect = ({
  refetch,
}: UseDashboardRefetchEffectOptions): void => {
  /**
   * Throttling the refetch function ensures it is only called once, in the
   * event of multiple calls in a short period of time. This can happen when the
   * pubSub event is fired multiple times when reconnecting to the WebSocket.
   */
  const throttledRefetch = useMemo(
    () => throttle(refetch, 1000, { leading: true, trailing: false }),
    [refetch],
  );

  useEffectOnce(() => {
    const unsubscribe = pubSub.subscribe(
      'real_time_dashboard:refetch',
      throttledRefetch,
    );

    return () => {
      unsubscribe();
      throttledRefetch.cancel();
    };
  });
};
